import { Glue42 } from "../../glue";
import { default as CallbackRegistryFactory, CallbackRegistry, UnsubscribeFunction } from "callback-registry";

const CommandMethod = "T42.Hotkeys.Command";
const InvokeMethod = "T42.Hotkeys.Invoke";

const RegisterCommand = "register";
const UnregisterCommand = "unregister";
const UnregisterAllCommand = "unregisterAll";

export class HotkeysImpl implements Glue42.Hotkeys.API {

    private registry: CallbackRegistry = CallbackRegistryFactory();
    private firstHotkey = true;
    private hotkeys = new Map<string, Glue42.Hotkeys.HotkeyInfo>();

    constructor(private agm: Glue42.AGM.API) {
    }

    public async register(info: string | Glue42.Hotkeys.HotkeyInfo, callback: Glue42.Hotkeys.HotkeyCallback): Promise<void> {
        if (typeof info === "undefined") {
            throw new Error("Hotkey parameter missing");
        }

        if (typeof info === "string") {
            info = {
                hotkey: info
            };
        } else {
            if (!info.hotkey) {
                throw new Error("Info's hotkey parameter missing");
            }

            info = {
                hotkey: info.hotkey,
                description: info.description
            };
        }

        const hkToLower = this.formatHotkey(info.hotkey);
        if (this.hotkeys.has(hkToLower)) {
            throw new Error(`Shortcut for ${hkToLower} already registered`);
        }

        // if very first a hotkey is registered by this application register invoke AGM method
        if (this.firstHotkey) {
            this.firstHotkey = false;
            await this.registerInvokeAGMMethod();
        }
        // store the callback
        this.registry.add(hkToLower, callback);

        // invoke GD method
        await this.agm.invoke(CommandMethod, { command: RegisterCommand, hotkey: hkToLower, description: info.description });

        this.hotkeys.set(hkToLower, info);
    }

    public async unregister(hotkey: string): Promise<void> {
        if (typeof hotkey === "undefined") {
            throw new Error("hotkey parameter missing");
        }

        if (typeof hotkey !== "string") {
            throw new Error("hotkey parameter must be string");
        }

        const hkToLower = this.formatHotkey(hotkey);
        await this.agm.invoke(CommandMethod, { command: UnregisterCommand, hotkey: hkToLower });
        this.hotkeys.delete(hkToLower);
        this.registry.clearKey(hkToLower);
    }

    public async unregisterAll(): Promise<void> {
        await this.agm.invoke(CommandMethod, { command: UnregisterAllCommand });
        this.hotkeys.clear();
        this.registry.clear();
    }

    /**
     * Whether this application has registered accelerator.
     * @param hotkey
     */
    public isRegistered(hotkey: string): boolean {
        const hkToLower = this.formatHotkey(hotkey);
        return this.hotkeys.has(hkToLower);
    }

    private registerInvokeAGMMethod() {
        return this.agm.register(InvokeMethod, (args: {
            key: string;
        }) => {
            const hkToLower = args.key.toLowerCase();
            const info = this.hotkeys.get(hkToLower);
            this.registry.execute(hkToLower, info);
        });
    }

    private formatHotkey(hotkey: string): string {
        if (hotkey) {
            return hotkey.replace(/\s/g, "").toLowerCase();
        }
    }
}
